import { useEffect, useMemo, useState } from "react";
import { Package, RotateCcw } from "@/components/icons";
import { Button, Input, Skeleton } from "@/components/kit";
import { useVariantStockLotsQuery } from "@/modules/inventory/presentation/api/queries";
import {
  buildFifoLotPicks,
  type OrderLineLotPick,
  sumLotPickQuantity,
} from "@/modules/orders/presentation/lib/order-lot-allocation";
import { formatQtyWithUnit } from "@/modules/products/domain/unit-conversion";
import { formatLak } from "@/shared/lib/currency";
import { formatDateTimeLocal } from "@/shared/lib/date-time";

export type StockAdjustLotPickerMode = "reduce" | "add" | "assign";

type StockAdjustLotPickerProps = {
  mode: StockAdjustLotPickerMode;
  variantId: string;
  stockUnitLabel: string;
  stockQuantity: number;
  /** reduceQty | addQty | max assign from without-lot */
  targetQty: number;
  picks: OrderLineLotPick[];
  onPicksChange: (picks: OrderLineLotPick[]) => void;
};

export function StockAdjustLotPicker({
  mode,
  variantId,
  stockUnitLabel,
  stockQuantity,
  targetQty,
  picks,
  onPicksChange,
}: StockAdjustLotPickerProps) {
  const lots = useVariantStockLotsQuery(variantId);
  const rows = lots.data?.data ?? [];
  const [fifoSeed, setFifoSeed] = useState(0);

  const lotTotal = useMemo(
    () => rows.reduce((sum, lot) => sum + lot.quantityRemaining, 0),
    [rows],
  );
  const withoutLot = Math.max(0, stockQuantity - lotTotal);
  const allocated = sumLotPickQuantity(picks);
  const lotMap = useMemo(
    () => new Map(rows.map((lot) => [lot.id, lot])),
    [rows],
  );

  const fromWithoutLot =
    mode === "reduce" ? Math.max(0, targetQty - allocated) : 0;

  const isValid =
    mode === "reduce"
      ? targetQty > 0 &&
        allocated <= targetQty &&
        fromWithoutLot <= withoutLot
      : mode === "add"
        ? targetQty > 0 && (allocated === 0 || allocated === targetQty)
        : allocated > 0 && allocated <= withoutLot;

  useEffect(() => {
    if (mode !== "reduce" || !lots.data || targetQty <= 0) {
      if (mode === "reduce") onPicksChange([]);
      return;
    }
    const target = Math.min(targetQty, lotTotal);
    onPicksChange(buildFifoLotPicks(lots.data.data, target));
  }, [mode, targetQty, lotTotal, lots.data, fifoSeed]);

  function resetFifo() {
    setFifoSeed((seed) => seed + 1);
  }

  function setLotQuantity(stockLotId: string, nextQty: number) {
    const lot = lotMap.get(stockLotId);
    if (!lot) return;

    const othersSum = picks
      .filter((pick) => pick.stockLotId !== stockLotId)
      .reduce((sum, pick) => sum + pick.quantity, 0);

    const maxQty =
      mode === "assign"
        ? Math.max(0, withoutLot - othersSum)
        : mode === "add"
          ? Math.max(0, targetQty - othersSum)
          : Math.min(lot.quantityRemaining, targetQty);

    const qty = Math.max(0, Math.min(nextQty, maxQty));
    const others = picks.filter((pick) => pick.stockLotId !== stockLotId);
    if (qty <= 0) {
      onPicksChange(others);
      return;
    }
    onPicksChange([
      ...others,
      {
        stockLotId: lot.id,
        quantity: qty,
        receiptId: lot.receiptId,
        receiptNumber: lot.receiptNumber,
        quantityRemaining: lot.quantityRemaining,
        unitLandedCost: lot.unitLandedCost,
      },
    ]);
  }

  if (lots.isLoading) {
    return <Skeleton className="h-32 rounded-xl" />;
  }

  if (rows.length === 0) {
    return (
      <p className="rounded-xl border border-dashed p-3 text-muted-foreground text-xs">
        {mode === "reduce"
          ? "ບໍ່ມີ lot — ຈະຫຼຸດຈາກສະຕັອກທີ່ບໍ່ມີ lot ເທົ່ານັ້ນ"
          : "ບໍ່ມີ lot — ໃຊ້ «ຮັບເຂົ້າ lot» ເພື່ອສ້າງ lot ໃໝ່"}
      </p>
    );
  }

  const summary =
    mode === "reduce" ? (
      <>
        <p>
          ຫຼຸດຈາກ lot{" "}
          <span
            className={
              isValid
                ? "font-semibold text-leaf"
                : "font-semibold text-destructive"
            }
          >
            {allocated}
          </span>
          {targetQty > 0 ? ` / ຫຼຸດທັງໝົດ ${targetQty}` : null}
        </p>
        {withoutLot > 0 ? (
          <p className="mt-1 text-muted-foreground">
            ສະຕັອກທີ່ບໍ່ມີ lot: {formatQtyWithUnit(withoutLot, stockUnitLabel)}
            {fromWithoutLot > 0
              ? ` · ຈະຫຼຸດ ${formatQtyWithUnit(fromWithoutLot, stockUnitLabel)}`
              : null}
          </p>
        ) : null}
      </>
    ) : mode === "add" ? (
      <>
        <p>
          ເພີ່ມເຂົ້າ lot{" "}
          <span
            className={
              isValid
                ? "font-semibold text-leaf"
                : "font-semibold text-destructive"
            }
          >
            {allocated}
          </span>
          {targetQty > 0 ? ` / ເພີ່ມທັງໝົດ ${targetQty}` : null}
        </p>
        {allocated === 0 ? (
          <p className="mt-1 text-muted-foreground">
            ຍັງບໍ່ເລືອກ lot — ຈະເພີ່ມເຂົ້າສະຕັອກທີ່ບໍ່ມີ lot
          </p>
        ) : allocated < targetQty ? (
          <p className="mt-1 text-destructive">
            ຕ້ອງແບ່ງໃຫ້ lot ຄົບ {targetQty} {stockUnitLabel}
          </p>
        ) : null}
      </>
    ) : (
      <>
        <p>
          ຈັດເຂົ້າ lot{" "}
          <span
            className={
              isValid
                ? "font-semibold text-leaf"
                : "font-semibold text-destructive"
            }
          >
            {allocated}
          </span>
          {withoutLot > 0
            ? ` / ສະຕັອກທີ່ບໍ່ມີ lot ${formatQtyWithUnit(withoutLot, stockUnitLabel)}`
            : null}
        </p>
      </>
    );

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-xl border border-secondary/20 bg-background/60 p-3 text-xs">
        {summary}
      </div>

      <ul className="flex max-h-48 flex-col gap-2 overflow-y-auto">
        {rows.map((lot) => {
          const pick = picks.find((row) => row.stockLotId === lot.id);
          const value = pick?.quantity ?? 0;
          const inputMax =
            mode === "reduce"
              ? Math.min(lot.quantityRemaining, targetQty)
              : mode === "add"
                ? targetQty
                : withoutLot;

          return (
            <li
              key={lot.id}
              className="rounded-xl border border-border/60 bg-muted/20 p-3"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Package className="size-3.5 shrink-0 text-muted-foreground" />
                    <span className="truncate font-medium text-sm">
                      {lot.receiptNumber ?? "ປະກອບຊຸດ"}
                    </span>
                  </div>
                  <p className="mt-0.5 text-muted-foreground text-xs">
                    {formatDateTimeLocal(lot.receivedAt)} · ຄົງ{" "}
                    {formatQtyWithUnit(lot.quantityRemaining, stockUnitLabel)} ·{" "}
                    {formatLak(lot.unitLandedCost)}/{stockUnitLabel}
                  </p>
                </div>
                <Input
                  type="number"
                  min={0}
                  max={inputMax}
                  value={value}
                  onChange={(e) =>
                    setLotQuantity(lot.id, Number(e.target.value) || 0)
                  }
                  className="h-9 w-20 tabular-nums"
                />
              </div>
            </li>
          );
        })}
      </ul>

      {mode === "reduce" ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start rounded-full"
          onClick={resetFifo}
          disabled={targetQty <= 0}
        >
          <RotateCcw data-icon="inline-start" />
          FIFO (ເກົ່າກ່ອນ)
        </Button>
      ) : null}
    </div>
  );
}

export function isStockAdjustLotSelectionValid(
  mode: StockAdjustLotPickerMode,
  stockQuantity: number,
  lotTotal: number,
  targetQty: number,
  picks: OrderLineLotPick[],
) {
  const allocated = sumLotPickQuantity(picks);
  const withoutLot = Math.max(0, stockQuantity - lotTotal);

  if (mode === "reduce") {
    if (targetQty <= 0) return true;
    const fromWithoutLot = targetQty - allocated;
    return allocated <= targetQty && fromWithoutLot <= withoutLot;
  }
  if (mode === "add") {
    if (targetQty <= 0) return true;
    return allocated === 0 || allocated === targetQty;
  }
  return allocated > 0 && allocated <= withoutLot;
}
